// ISDP CUSTOM: Global confirmation dialog. A provider mounted at app root exposes
// useConfirm() → confirm(options) => Promise<boolean>. Gives every destructive
// action ("are you sure?") a consistent, ODX-styled, dark-mode-aware modal instead
// of ad-hoc window.confirm() or missing confirmation.
import { useCallback, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import {
  ConfirmContext,
  useConfirm,
  type ConfirmFn,
  type ConfirmOptions,
} from '@internal/plugin-isdp-common/ui';
import { feedbackMessages } from '../../i18n/feedback';
import { commonMessages } from '../../i18n/common';
import './ConfirmProvider.css';

// Re-exported so existing in-app imports (`../feedback/ConfirmProvider`) keep working;
// the context + hook now live in isdp-common so the admin plugin shares them.
export { useConfirm };

interface PendingState extends ConfirmOptions {
  resolve: (ok: boolean) => void;
}

export const ConfirmProvider = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslationRef(feedbackMessages);
  const { t: tc } = useTranslationRef(commonMessages);
  const [pending, setPending] = useState<PendingState | null>(null);

  const confirm = useCallback<ConfirmFn>(options => {
    return new Promise<boolean>(resolve => {
      setPending({ ...options, resolve });
    });
  }, []);

  const close = useCallback((ok: boolean) => {
    setPending(prev => {
      prev?.resolve(ok);
      return null;
    });
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {pending &&
        createPortal(
          <div
            className="devhub-confirm__backdrop"
            role="button"
            tabIndex={0}
            aria-label={t('dismissDialog')}
            onClick={() => close(false)}
            onKeyDown={e => { if (e.key === 'Escape' || e.key === 'Enter' || e.key === ' ') { e.preventDefault(); close(false); } }}
          >
            <div
              className="devhub-confirm"
              role="presentation"
              aria-label={pending.title}
              onClick={e => e.stopPropagation()}
              onKeyDown={e => e.stopPropagation()}
            >
              <h2 className="devhub-confirm__title">{pending.title}</h2>
              {pending.message && (
                <div className="devhub-confirm__message">{pending.message}</div>
              )}
              <div className="devhub-confirm__actions">
                <button
                  type="button"
                  className="devhub-confirm__btn"
                  onClick={() => close(false)}
                >
                  {pending.cancelLabel ?? tc('actions.cancel')}
                </button>
                <button
                  type="button"
                  className={`devhub-confirm__btn devhub-confirm__btn--${
                    pending.danger === false ? 'primary' : 'danger'
                  }`}
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus
                  onClick={() => close(true)}
                >
                  {pending.confirmLabel ?? tc('actions.delete')}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </ConfirmContext.Provider>
  );
};
